import { apiAuth, apiResponseType } from ".";

/** Banco de Bolões > Formador de Bolões (LOT-62): "Montar bolões" e "Bolões escolhidos" */

export interface SelectionLottery {
  id: number;
  name: string;
  color: string | null;
}

export interface SelectionGame {
  id: number;
  game_number: number;
  dezenas: number;
  numbers: number[];
}

export interface SelectionBolao {
  id?: number;
  position: number;
  games_count: number;
  dezenas: number;
  quotas: number;
  games: SelectionGame[];
}

export interface SelectionDetail {
  id: number;
  uuid: string;
  lottery: SelectionLottery;
  contest: { id: number; name: string };
  created_at: string;
  boloes: SelectionBolao[];
}

export interface SelectionListItem {
  id: number;
  uuid: string;
  lottery: SelectionLottery | null;
  contest: { id: number; name: string } | null;
  boloes_count: number;
  games_count: number;
  created_at: string;
}

export interface SelectionListPage {
  data: SelectionListItem[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface SelectionAvailableGamesPage {
  data: SelectionGame[];
  current_page: number;
  last_page: number;
  total: number;
}

export interface SelectionProductOptions {
  games: number[];
  quotas: number[];
  dezenas: number[];
}

/** Máximo de bolões numa seleção (o back valida o mesmo limite) */
export const MAX_SELECTION_BOLOES = 1500;

/** Corpo de salvar/pré-visualizar: os bolões levam só os ids dos jogos do banco */
export interface SelectionPayload {
  lottery_id: number;
  contest_id: number;
  selection_uuid?: string;
  boloes: Array<{
    games_count: number;
    dezenas: number;
    quotas: number;
    game_ids: number[];
  }>;
}

export const getSelectionLotteries = (): apiResponseType =>
  apiAuth.get("game-bank/selection-lotteries");

export const getSelectionProductOptions = (lotteryId: number): apiResponseType =>
  apiAuth.get(`game-bank/product-options?lottery_id=${lotteryId}`);

/**
 * Jogos do banco disponíveis para um bolão. POST porque exclude_ids (jogos já escolhidos na tela) pode ter
 * centenas de ids e estouraria o limite de tamanho da URL num GET.
 */
export const getSelectionAvailableGames = (params: {
  lottery_id: number;
  dezenas: number;
  selection_uuid?: string;
  exclude_ids?: number[];
  search?: string;
  page?: number;
  per_page?: number;
}): apiResponseType =>
  apiAuth.post("game-bank/available-games", {
    lottery_id: params.lottery_id,
    dezenas: params.dezenas,
    selection_uuid: params.selection_uuid || undefined,
    exclude_ids: params.exclude_ids ?? [],
    search: params.search || undefined,
    page: params.page,
    per_page: params.per_page,
  });

export const getSelections = (params: {
  lottery_id?: number;
  search?: string;
  page?: number;
  per_page?: number;
}): apiResponseType => {
  const query = new URLSearchParams();
  if (params.lottery_id) query.append("lottery_id", String(params.lottery_id));
  if (params.search) query.append("search", params.search);
  if (params.page) query.append("page", String(params.page));
  if (params.per_page) query.append("per_page", String(params.per_page));

  return apiAuth.get(`game-bank/selections?${query.toString()}`);
};

export const getSelection = (uuid: string): apiResponseType =>
  apiAuth.get(`game-bank/selections/${uuid}`);

export const createSelection = (payload: SelectionPayload): apiResponseType =>
  apiAuth.post("game-bank/selections", payload);

export const updateSelection = (uuid: string, payload: SelectionPayload): apiResponseType =>
  apiAuth.put(`game-bank/selections/${uuid}`, payload);

export const deleteSelection = (uuid: string): apiResponseType =>
  apiAuth.delete(`game-bank/selections/${uuid}`);

export const duplicateSelection = (uuid: string, contestId: number): apiResponseType =>
  apiAuth.post(`game-bank/selections/${uuid}/duplicate`, { contest_id: contestId });

/**
 * Forma de gerar o PDF: "fast" desenha o mesmo layout direto (1000 bolões em cerca de 1 s); "standard" monta HTML
 * (o de antes, lento com muitos bolões).
 */
export type PdfMode = "fast" | "standard";

/** PDF de uma seleção salva. Em erro o interceptor já mostra o toast e não devolve arquivo. */
export const downloadSelectionPdf = (uuid: string, mode: PdfMode = "standard"): Promise<Blob | null> =>
  apiAuth
    .get(`game-bank/selections/${uuid}/${mode === "fast" ? "pdf-fast" : "pdf"}`, { responseType: "blob" })
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));

/** PDF do que está na tela (antes de salvar) */
export const previewSelectionPdf = (payload: SelectionPayload, mode: PdfMode = "standard"): Promise<Blob | null> =>
  apiAuth
    .post(`game-bank/selections/${mode === "fast" ? "preview-pdf-fast" : "preview-pdf"}`, payload, {
      responseType: "blob",
    })
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));
